import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, message, Divider } from 'antd';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import type { AudioAsset, UpdateAudioRequest, AudioAssetType } from '../../types';

const { Option } = Select;
const { TextArea } = Input;

interface EditAudioModalProps {
  visible: boolean;
  audio: AudioAsset;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditAudioModal: React.FC<EditAudioModalProps> = ({
  visible,
  audio,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const typeValue = Form.useWatch('type', form);

  useEffect(() => {
    if (visible && audio) {
      const rawMood = audio.mood || [];
      const normalizedMood = rawMood.flatMap(m => m.includes(',') ? m.split(',').map(s => s.trim()).filter(Boolean) : [m]);

      form.setFieldsValue({
        name: audio.name,
        description: audio.description,
        type: audio.type || 'music',
        bpm: audio.bpm,
        mood: normalizedMood,
        flow: audio.flow,
        instruments: audio.instruments || [],
        genre: audio.genre,
        layering: audio.layering
      });
    }
  }, [visible, audio, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data: UpdateAudioRequest = {
        name: values.name,
        description: values.description,
        type: values.type,
        bpm: values.bpm,
        mood: values.mood || [],
        flow: values.flow,
        instruments: values.instruments || [],
        genre: values.genre,
        layering: values.layering
      };

      await audioLibraryApi.updateAudio(audio.id, data);
      
      message.success('Audio updated successfully');
      onSuccess();
    } catch (error: any) {
      message.error('Failed to update audio: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const moodOptions = ['uplifting', 'dramatic', 'tense', 'peaceful', 'sad', 'joyful', 'mysterious', 'romantic', 'epic', 'melancholic'];

  return (
    <Modal
      title={`Edit Audio: ${audio.name}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={saving}
      width={600}
      okText="Save"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter audio name' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Select>
            <Option value="music">Music</Option>
            <Option value="sound_effect">Sound Effect</Option>
          </Select>
        </Form.Item>

        {typeValue !== 'sound_effect' && (
          <>
            <Divider>Music Properties</Divider>

            <Form.Item name="genre" label="Genre">
              <Input placeholder="e.g., cinematic, electronic, orchestral" />
            </Form.Item>

            <Form.Item name="instruments" label="Instruments">
              <Select
                mode="tags"
                placeholder="Add instruments (press Enter after each)"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="mood" label="Mood">
              <Select mode="tags" placeholder="Select or type mood(s)">
                {moodOptions.map(mood => (
                  <Option key={mood} value={mood}>{mood}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="bpm" label="BPM (Beats Per Minute)">
              <InputNumber min={0} max={300} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="layering" label="Layering">
              <TextArea rows={2} placeholder="Describe the layering structure" />
            </Form.Item>

            <Form.Item name="flow" label="Flow">
              <TextArea rows={3} placeholder="Describe how the audio flows/progresses" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditAudioModal;
